import { useEffect, useState } from "react"
import { createPortal } from "react-dom";
import { FaClipboard, FaSync } from "react-icons/fa";
import styled from "styled-components";
import { Button, LoadingIcon, Modal } from "@prosolve/common-components-ui"
import useMe from "../../hooks/useMe";
import ActionButton from "../Shared/ActionButton";
import useFetchOpportunities from "../Shared/useFetchOpportunities";
import useFetchOrders from "../Shared/useFetchOrders";
import useShipmentSnackbar from "../Shared/useShipmentSnackbar";
import { useRefreshOpportunity } from "./RefreshOpportunityButton";
import OpportunityCard from "./OpportunityCard";
import OrderList from "../Orders/OrderList";

const Row = styled.div`
  display: flex;
  gap: 15px;
  padding-bottom: 15px;
  padding-right: 15px;
  scrollbar-gutter: stable;
  overflow: hidden; 

  &.scrollable {
    overflow: auto; 
    height: 90%;
    border-top: solid 1px rgb(0 0 0 / 10%);
    border-bottom: solid 1px rgb(0 0 0 / 10%);
    padding: 10px 15px 10px 0;
  }
`

const Column = styled.div`
  flex: 1;

  &.sticky {
    position: sticky;
    top: 0;
  }
`

const ColumnHeader = styled.div`
  flex: 1;
  font-size: 16px;
  font-weight: bold;
  color: #565656;
  padding-left: 5px;
`

export function ManageOpportunity ({ 
  opportunity: opportunityParam,
  opportunityId: opportunityIdParam,
  shipModals,
  onOpportunityUpdate
}) {
  //console.log('isManageModalOpen .opportunityId', opportunityIdParam, ', .opportunityParam', opportunityParam, 'onOpportunityUpdate', onOpportunityUpdate)
  const [opportunity, setOpportunity] = useState(opportunityParam)

  const [isLoadingOpp, setIsLoadingOpp] = useState(!opportunity)
  const [isLoading, setIsLoading] = useState(true)
  const [orders, setOrders] = useState()

  const shipmentSnackbar = useShipmentSnackbar()
  const { fetchOpportunities: fetchOpportunitiesHook } = useFetchOpportunities()
  const fetchOpportunityRefreshHook = useRefreshOpportunity()
  const { fetchOrders: fetchOrdersHook } = useFetchOrders()

  const opportunityId = opportunity?.Id || opportunityIdParam

  const { hasShipmentTrackingRead: hasRead } = useMe()

  const fetchOpportunities = async () => {
    setIsLoadingOpp(true)
    try {
      const resultJson = await fetchOpportunitiesHook({ opportunityId })
      setOpportunity(resultJson[0])
    } finally {
      setIsLoadingOpp(false)
    }
  }

  const fetchOpportunityRefresh = async () => {
    setIsLoadingOpp(true)
    try {
      await fetchOpportunityRefreshHook(opportunityId)
    } finally {
      setIsLoadingOpp(false)
    }
  }

  const fetchOrders = async (options = {}) => {
    setIsLoading(true)
    try {
      const orders = await fetchOrdersHook({ opportunityId, ...options })
      setOrders(orders.error ? [] : orders)
    } finally {
      setIsLoading(false)
    }
  }

  const onOpen = () => {
    const fetches = async () => {
      var asyncs = []
      if (!opportunity) {
        asyncs.push(fetchOpportunities())
      }
      if (!orders) {
        asyncs.push(fetchOrders())
      }
      await Promise.all(asyncs)
    }
    fetches()
  }

  const onOrdersUpdate = async () => {
    await fetchOrders()
    await fetchOpportunities()
  }

  const updateSbOrderData = async () => {
    // Orders update in place
    //fetchOrders()
    await fetchOpportunityRefresh()
    await fetchOpportunities()
  }

  const onRefreshClick = async () => {
    try {
      const promiseOrders = fetchOrders({ useCache: false })
      await fetchOpportunityRefresh()
      await fetchOpportunities()
      await promiseOrders
    } catch(e) {
      console.error(e)
      shipmentSnackbar.newMessage({ message: 'Error refreshing', severity: 'failure' })
    }
  }

  useEffect(() => {
    onOpen()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setOpportunity(opportunityParam)
  }, [opportunityParam])

  useEffect(() => {
    fetchOpportunities()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityIdParam])

  return (<>
    <Row>
      <ColumnHeader>Opportunity Details</ColumnHeader>
      <ColumnHeader style={{ flex: '2' }}>Orders {orders?.length ? `(${orders.length})` : ''}</ColumnHeader>
      {hasRead &&
        <FaSync title={'Refresh'} onClick={onRefreshClick} style={{ cursor: 'pointer' }} />
      }
    </Row>
    <Row className="scrollable">
      <Column className="sticky">
        {
          isLoadingOpp ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
              <LoadingIcon />
            </div>
          ) : (
            <OpportunityCard 
              opportunity={opportunity} 
              showManageButton={false} 
              showRefreshButton={false} 
              onCreateOrder={onOrdersUpdate} 
              onOpportunityUpdate={onOpportunityUpdate} 
              maxLineItemsHeight={'calc(100vh - 335px)'} />
          )
        }
      </Column>
      <Column style={{ flex: '2' }}>
        {
          isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
              <LoadingIcon />
            </div>
          ) : ((orders || []).length > 0) ? (
            <OrderList 
              orders={orders} 
              shipModals={shipModals} 
              updateOrderData={onOrdersUpdate} 
              updateSbOrderData={updateSbOrderData} 
              showRefreshButton={false} 
              showOpportunityButton={false}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.1, height: '500px' }}>
              <div style={{ fontSize: '100px', lineHeight: 0 }}><FaClipboard /></div>
              <div style={{ fontSize: '24px' }}>No Orders</div>
            </div>
          )
        }
      </Column>
    </Row>
  </>)
}

export function ManageOpportunityModal ({
  onClose: onCloseParam, 
  opportunity,
  opportunityId,
  shipModals,
  onOpportunityUpdate
}) {

  const onOpen = () => {
    document.documentElement.style.overflow = 'hidden'
  }

  const onClose = () => {
    onCloseParam?.(false)
    document.documentElement.style.overflow = 'auto'
  }

  useEffect(() => {
    onOpen()
  }, [])
  
  const footer = (
    <div style={{ 
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      columnGap: '10px',
      fontSize: '16px',
      fontWeight: 'normal',
      padding: '10px'
    }}>
      <Button onClick={onClose} $colored={true}>
        {'Close'}
      </Button>
    </div>
  )

  const modal = (
    <Modal title={'Manage Opportunity'} onClose={onClose} footer={footer} isFullScreen={true}>
      <ManageOpportunity
        onClose={onClose}
        opportunity={opportunity}
        opportunityId={opportunityId}
        shipModals={shipModals}
        onOpportunityUpdate={onOpportunityUpdate} />
    </Modal>
  )

  return (<>
    {createPortal(modal, document.body)}
  </>)
}

export default function ManageOpportunityButton ({
  opportunity,
  opportunityId,
  shipModals
}) {
  const { setManageOppOpportunity, setManageOppOpportunityId } = shipModals

  const openManageOpp = async () => {
    setManageOppOpportunity(opportunity)
    setManageOppOpportunityId(opportunityId)
  }

  return (
    <>
      <ActionButton onClick={openManageOpp}>Manage Opportunity</ActionButton>
    </>
  )
}